import type {
  InterceptorManagerConfig,
  InterceptorType,
  InternalRequestConfig,
  RequestInterceptor,
  RequestInterceptorObject,
  RequestSkipperCallback,
  ResponseInterceptor,
  ResponseInterceptorObject,
  ResponseSkipperCallback,
  TrResponse,
} from './types'
import { isNullOrUndefined } from './utils'

const defaultSkipper = () => false

export class InterceptorManager {
  #responseInterceptors: ResponseInterceptorObject[]

  #requestInterceptors: RequestInterceptorObject[]

  #responseSkipper: ResponseSkipperCallback

  #requestSkipper: RequestSkipperCallback

  constructor(cfg?: Partial<InterceptorManagerConfig>) {
    this.#requestInterceptors = cfg?.requestInterceptors ?? []
    this.#responseInterceptors = cfg?.responseInterceptors ?? []
    this.#requestSkipper = cfg?.requestSkipper ?? defaultSkipper
    this.#responseSkipper = cfg?.responseSkipper ?? defaultSkipper
  }

  clone() {
    return new InterceptorManager({
      requestInterceptors: [...this.#requestInterceptors],
      requestSkipper: this.#requestSkipper,
      responseInterceptors: [...this.#responseInterceptors],
      responseSkipper: this.#responseSkipper,
    })
  }

  addRequestInterceptor(interceptor: RequestInterceptorObject) {
    this.#requestInterceptors.push(interceptor)
  }

  addResponseInterceptor<T = unknown>(interceptor: ResponseInterceptorObject<T>) {
    this.#responseInterceptors.push(interceptor)
  }

  #clearInterceptors<T extends { type?: InterceptorType }>(
    interceptors: T[],
    iType?: InterceptorType,
  ): void {
    if (iType) {
      const filtered = interceptors.filter((item) => item.type !== iType)

      interceptors.splice(0, interceptors.length, ...filtered)
    } else {
      interceptors.splice(0)
    }
  }

  clearResponseInterceptors(iType?: InterceptorType) {
    this.#clearInterceptors(this.#responseInterceptors, iType)
  }

  clearRequestInterceptors(iType?: InterceptorType) {
    this.#clearInterceptors(this.#requestInterceptors, iType)
  }

  #findInterceptorIndex<T extends RequestInterceptor | ResponseInterceptor>(
    interceptors: { callback: T; type?: InterceptorType }[],
    interceptor: T,
  ): number {
    return typeof interceptor === 'function'
      ? interceptors.findIndex((item) => item.callback === interceptor)
      : interceptors.findIndex(({ callback, type }) => {
          return type === interceptor.type && callback === interceptor.callback
        })
  }

  removeRequestInterceptor(interceptor: RequestInterceptor): void {
    const index = this.#findInterceptorIndex(this.#requestInterceptors, interceptor)

    if (index > -1) {
      this.#requestInterceptors.splice(index, 1)
    }
  }

  removeResponseInterceptor(interceptor: ResponseInterceptor): void {
    const index = this.#findInterceptorIndex(this.#responseInterceptors, interceptor)

    if (index > -1) {
      this.#responseInterceptors.splice(index, 1)
    }
  }

  setResponseSkipper(skipper: ResponseSkipperCallback) {
    this.#responseSkipper = skipper
  }

  setRequestSkipper(skipper: RequestSkipperCallback) {
    this.#requestSkipper = skipper
  }

  doRequestIntercept(
    config: InternalRequestConfig,
    iType?: InterceptorType,
  ): Promise<InternalRequestConfig> | InternalRequestConfig {
    if (this.#requestSkipper(config)) {
      return config
    }

    const observers = this.#requestInterceptors.filter((item) => {
      return isNullOrUndefined(item.type) || item.type === iType
    })

    if (observers.length === 0) {
      return config
    }

    return observers.reduce(async (promise, observer) => {
      return promise.then(async (prevConfig) => {
        const newConfig = await Promise.resolve(observer.callback(prevConfig))

        return newConfig ?? prevConfig
      })
    }, Promise.resolve(config))
  }

  doResponseIntercept(response: TrResponse, iType?: InterceptorType): any {
    if (this.#responseSkipper(response)) {
      return response
    }

    const observers = this.#responseInterceptors.filter((item) => {
      return isNullOrUndefined(item.type) || item.type === iType
    })

    if (observers.length === 0) {
      return response
    }

    return observers.reduce(
      (promise, observer) => promise.then((prevData) => observer.callback(prevData)) as any,
      Promise.resolve(response),
    )
  }

  /**
   * @internal
   */
  get requestInterceptors() {
    return this.#requestInterceptors
  }

  /**
   * @internal
   */
  get responseInterceptors() {
    return this.#responseInterceptors
  }

  /**
   * @internal
   */
  get requestSkipper() {
    return this.#requestSkipper
  }

  /**
   * @internal
   */
  get responseSkipper() {
    return this.#responseSkipper
  }
}
